/* eslint-disable react/prop-types */
import { useState, useCallback } from "react";
import API, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Broom, CaretDown, Warning } from "@phosphor-icons/react";

/**
 * Collapsible panel that surfaces emails auto-matched to this case by
 * the legacy (July 2026 pre-fix) address key, but which the current
 * stricter matcher would no longer match. Solicitor selects rows and
 * clicks Detach to bulk-remove them from the case AND suppress them
 * from being auto-attached again on future inbox syncs.
 *
 * Backed by:
 *   GET  /api/cases/{caseId}/communications/address-mismatches
 *   POST /api/cases/{caseId}/communications/unmatch-batch
 *
 * Renders nothing (a compact "0 mismatches" state) when clean —
 * solicitors don't need noise when everything's healthy.
 */
export default function EmailBatchCleanupPanel({ caseId, onDetached }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [detaching, setDetaching] = useState(false);
  const [mismatches, setMismatches] = useState([]);
  const [addressKey, setAddressKey] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const fetchMismatches = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/cases/${caseId}/communications/address-mismatches`);
      setMismatches(Array.isArray(data.mismatches) ? data.mismatches : []);
      setAddressKey(data.address_key || null);
      setSelected(new Set());
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to load mismatches");
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  const toggle = () => {
    const next = !open;
    setOpen(next);
    if (next && mismatches.length === 0) fetchMismatches();
  };

  const toggleRow = (commId) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(commId)) next.delete(commId);
      else next.add(commId);
      return next;
    });
  };

  const toggleAll = () => {
    if (selected.size === mismatches.length) {
      setSelected(new Set());
    } else {
      setSelected(new Set(mismatches.map((m) => m.comm_id)));
    }
  };

  const detachSelected = async () => {
    if (selected.size === 0) return;
    if (!window.confirm(`Detach ${selected.size} email${selected.size === 1 ? "" : "s"} from this case? They'll stay in Outlook but won't re-attach on future syncs.`)) {
      return;
    }
    setDetaching(true);
    try {
      const { data } = await API.post(`/cases/${caseId}/communications/unmatch-batch`, {
        comm_ids: Array.from(selected),
      });
      toast.success(`Detached ${data.removed} email${data.removed === 1 ? "" : "s"}`);
      await fetchMismatches();
      if (onDetached) onDetached();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to detach emails");
    } finally {
      setDetaching(false);
    }
  };

  return (
    <div className="mb-3 border border-slate-200 rounded-md bg-slate-50" data-testid="email-cleanup-panel">
      <button
        type="button"
        onClick={toggle}
        className="w-full px-3 py-2 flex items-center justify-between text-xs font-medium text-slate-700 hover:bg-slate-100 rounded-md"
        data-testid="email-cleanup-toggle"
      >
        <span className="flex items-center gap-1.5">
          <Broom weight="bold" className="w-3.5 h-3.5 text-slate-500" />
          Bulk cleanup — mismatched emails
          {mismatches.length > 0 && (
            <span className="px-1.5 py-0.5 text-[10px] rounded bg-amber-100 text-amber-800 font-semibold" data-testid="email-cleanup-count-badge">
              {mismatches.length}
            </span>
          )}
        </span>
        <CaretDown weight="bold" className={`w-3 h-3 text-slate-400 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>

      {open && (
        <div className="px-3 pb-3 pt-1 border-t border-slate-200" data-testid="email-cleanup-body">
          <div className="mb-2 flex items-start gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-[11px] text-amber-900">
            <Warning weight="bold" className="w-3.5 h-3.5 shrink-0 mt-0.5" />
            <span>
              These emails are on this file but actually name a <strong>different</strong> property — usually a reply-all chain that threaded onto the wrong matter. Review and detach any that don't belong here. Emails that mention this case's own address aren't listed.
              {addressKey && <> This case: <code className="px-1 bg-white rounded border">{addressKey}</code></>}
            </span>
          </div>

          {loading ? (
            <p className="text-xs text-slate-400 py-3 text-center" data-testid="email-cleanup-loading">Checking for mismatches…</p>
          ) : mismatches.length === 0 ? (
            <p className="text-xs text-slate-500 py-3 text-center" data-testid="email-cleanup-empty">
              No mismatched emails on this case — everything looks clean.
            </p>
          ) : (
            <>
              <div className="flex items-center justify-between mb-2">
                <label className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={selected.size === mismatches.length}
                    onChange={toggleAll}
                    className="h-3.5 w-3.5 rounded border-slate-300"
                    data-testid="email-cleanup-select-all"
                  />
                  Select all ({mismatches.length})
                </label>
                <div className="flex items-center gap-1.5">
                  <Button
                    onClick={fetchMismatches}
                    variant="outline"
                    size="sm"
                    className="h-7 text-[11px]"
                    disabled={loading}
                    data-testid="email-cleanup-refresh"
                  >
                    Refresh
                  </Button>
                  <Button
                    onClick={detachSelected}
                    size="sm"
                    className="h-7 text-[11px] gap-1 text-white bg-red-600 hover:bg-red-700"
                    disabled={selected.size === 0 || detaching}
                    data-testid="email-cleanup-detach-btn"
                  >
                    {detaching ? "Detaching…" : `Detach selected (${selected.size})`}
                  </Button>
                </div>
              </div>

              <ul className="divide-y divide-slate-200 border border-slate-200 rounded bg-white" data-testid="email-cleanup-list">
                {mismatches.map((m) => {
                  const when = m.sent_at ? new Date(m.sent_at) : null;
                  return (
                    <li
                      key={m.comm_id}
                      className="flex items-start gap-2 px-2 py-2 hover:bg-slate-50"
                      data-testid={`email-cleanup-row-${m.comm_id}`}
                    >
                      <input
                        type="checkbox"
                        checked={selected.has(m.comm_id)}
                        onChange={() => toggleRow(m.comm_id)}
                        className="mt-1 h-3.5 w-3.5 rounded border-slate-300"
                        data-testid={`email-cleanup-check-${m.comm_id}`}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${m.direction === "incoming" ? "bg-blue-50 text-blue-700" : "bg-slate-100 text-slate-600"}`}>
                            {m.direction === "incoming" ? "IN" : "OUT"}
                          </span>
                          <p className="text-xs font-medium text-slate-900 truncate flex-1">{m.subject || "(no subject)"}</p>
                          {when && (
                            <span className="text-[10px] text-slate-400 shrink-0">
                              {when.toLocaleDateString("en-GB", { day: "2-digit", month: "short" })}
                            </span>
                          )}
                        </div>
                        <p className="text-[11px] text-slate-500 truncate">
                          {m.from_email || "unknown"} — {m.body_preview}
                        </p>
                        {m.looks_like && (
                          <p className="text-[10px] text-amber-700 mt-0.5 truncate" data-testid={`email-cleanup-looks-like-${m.comm_id}`}>
                            Looks like: <span className="font-medium">{m.looks_like}</span>
                            {m.match_source ? <span className="text-slate-400"> · matched by {m.match_source}</span> : null}
                          </p>
                        )}
                      </div>
                    </li>
                  );
                })}
              </ul>
            </>
          )}
        </div>
      )}
    </div>
  );
}
